
export enum UserRole {
  SUPER_ADMIN = 'super_admin', // You (The Platform Owner)
  ADMIN = 'admin', // The Business Owner (Cafe Manager)
  EMPLOYEE = 'employee', // Staff
}

export type UserStatus = 'pending' | 'active' | 'suspended';
export type SubscriptionStatus = 'active' | 'trial' | 'past_due' | 'cancelled' | 'suspended' | 'payment_unlinked' | 'pending_payment';

/**
 * Tenant represents a business account in CafeMaster Pro
 * 
 * BILLING RULES:
 * - priceId is assigned by Super Admin during onboarding (admin cannot change)
 * - Admin can only pay for the assigned plan, not select a different one
 * - Only Super Admin can modify priceId via tenant management
 */
export interface Tenant {
  id: string;
  name: string; // Business Name
  businessName?: string; // Alias for name (backward compatibility)
  ownerEmail: string; // The email of the person who bought the sub
  email?: string; // Alias for ownerEmail (backward compatibility)
  adminEmail?: string; // Admin user's email
  adminName?: string; // Admin user's name
  subscriptionStatus: SubscriptionStatus;
  subscriptionCost: number; // Monthly cost for this specific store
  priceId?: string; // Stripe Price ID - assigned by super admin, admin cannot change
  stripeCustomerId?: string; // Stripe Customer ID (created after first payment)
  stripeSubscriptionId?: string; // Stripe Subscription ID (created after payment)
  currentPeriodEnd?: number | null; // When the current billing period ends
  trialEnd?: number | null; // When the trial ends (if on trial)
  status?: string;
  graceUntil?: number | null;
  source?: string;
  cancelAtPeriodEnd?: boolean | null; // True if subscription will cancel at period end
  // Cancellation request fields (for admin request -> super admin approval flow)
  cancellationRequestedAt?: number | null;
  cancellationRequestedBy?: string | null; // userId or email of requester
  cancellationRequestReason?: string | null;
  cancellationRequestStatus?: 'pending' | 'approved' | 'denied' | null;
  cancellationDeniedAt?: number | null;
  cancellationDeniedBy?: string | null;
  cancellationDeniedReason?: string | null;
  createdAt: number;
  // Multi-product package management fields
  productType?: string; // Product the tenant belongs to (e.g., 'cafemaster')
  dealCode?: string; // Deal code used at signup (if any)
  enabledPackages?: string[]; // Array of enabled package IDs
  /** Locations included free (2026-08-26 spec: seeded from the deal at signup; admin-editable). Readers default `?? 1`. */
  freeLocationQuota?: number;
  legacyPlan?: boolean; // True if tenant was created before package system
  /**
   * Set by billing-service when a subscription sync (package change -> Stripe,
   * design §7/D9) could not be applied. The entitlement write already
   * succeeded when this is set — it means billing did not follow. Cleared on
   * the next successful sync. Surface persistently; a toast alone is not
   * enough for a billing failure.
   */
  billingSyncPending?: boolean;
  billingSyncError?: string;
  billingSyncAt?: number;
  /**
   * The tenant is billing CORRECTLY in Stripe, but we could not write the
   * internal record of how that price was arrived at (their `PlanAssignment`).
   *
   * Set by `POST /onboarding/complete` when a completion whose money had
   * already moved could not record itself, and cleared only by a sync that
   * actually runs. It rides on the same banner as `billingSyncPending` because
   * it is the same question — "is this tenant's billing in order?" — but it
   * points at a DIFFERENT system, so the banner must not tell an operator that
   * Stripe failed when Stripe is healthy.
   */
  billingRecordGap?: boolean;
}

export interface PricePlan {
  id: string;
  nickname?: string;
  unitAmount: number | null;
  currency: string;
  interval: string;
  intervalCount?: number;
  productId?: string;
  productName?: string;
  active: boolean;
}

export interface BusinessRequest {
  id: string;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  status: 'new' | 'contacted' | 'onboarded';
  onboardedTenantId?: string; // Added to track assigned ID
  submittedAt: number;
}

// CRM lead captured before onboarding; mirrors backend lead shape
export interface Lead {
  id: string;
  businessName: string;
  email: string;
  name?: string; // backend uses name while UI uses contactName
  contactName?: string;
  phone?: string;
  status?: 'new' | 'contacted' | 'onboarded' | 'closed';
  onboardedTenantId?: string;
  submittedAt?: number;
}

export interface User {
  id: string;
  tenantId: string; // Links user to a specific business (active tenant)
  allTenantIds?: string[]; // All tenants this user has access to (for multi-business users)
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  password?: string;
  completedTrainingRecipeIds: string[];
}

// Minimal tenant info for the business switcher
export interface TenantSummary {
  id: string;
  name: string;
  logoUrl?: string;
  userRole?: string; // User's role in this tenant (admin/employee)
}

export interface TrainingProgram {
  id: string;
  tenantId: string;
  employeeId: string;
  title: string;
  startDate: string;
  endDate: string;
  recipeIds: string[];
  assignedBy: string;
  status: 'pending' | 'active' | 'completed';
}

export interface MeasurementUnit {
  id: string;
  tenantId: string;
  name: string;
  abbreviation: string;
  unitType?: 'recipe' | 'inventory';  // Category: 'recipe' for recipe measurements, 'inventory' for stock tracking. Default/empty = 'recipe'
  imageUrl?: string;  // AI-generated image URL
  imageJobId?: string;  // Job ID for pending image generation
  imageStatus?: 'pending' | 'processing' | 'completed' | 'failed';  // Image generation status
}

export interface Category {
  id: string;
  tenantId: string;
  name: string;
  imageUrl?: string;  // AI-generated image URL
  imageJobId?: string;  // Job ID for pending image generation
  imageStatus?: 'pending' | 'processing' | 'completed' | 'failed';  // Image generation status
}

export interface InventoryItem {
  id: string;
  tenantId: string;
  name: string;
  unitId: string;              // Recipe unit (e.g., "spoon" for recipes)
  costPerUnit: number;
  stockQuantity?: number;      // Current stock level
  stockUnitId?: string;        // Inventory unit (e.g., "bags" for stock tracking) - can be different from recipe unit
  imageUrl?: string;           // AI-generated image URL
  imageJobId?: string;         // Job ID for pending image generation
  imageStatus?: 'pending' | 'processing' | 'completed' | 'failed';  // Image generation status
  // Stock status fields (for inventory management)
  adminMessage?: string;       // Status message from admin (visible to employees)
  adminMessageAt?: number;     // Timestamp when message was set
  stockStatus?: 'normal' | 'low' | 'out_of_stock' | 'ordered';  // Current stock status
}

// --- SUPPLIES (cups, napkins, lids, etc.) ---

export interface SupplySize {
  id: string;
  name: string;       // e.g., "12oz", "16oz", "Limited Edition", "Design Cup"
  cost: number;       // Cost per unit for this size
  stockQuantity?: number;  // Stock quantity for this variation
  stockUnitId?: string;    // Unit of measure for stock
}

export interface Supply {
  id: string;
  tenantId: string;
  name: string;       // e.g., "Paper Cup", "Plastic Lid", "Napkin"
  sizes: SupplySize[];
  stockQuantity?: number;  // Current stock quantity
  stockUnitId?: string;    // Unit of measure for stock (e.g., "boxes" while selling individual items)
  imageUrl?: string;  // AI-generated image URL
  imageJobId?: string;  // Job ID for pending image generation
  imageStatus?: 'pending' | 'processing' | 'completed' | 'failed';  // Image generation status
  // Stock status fields (for inventory management)
  adminMessage?: string;
  adminMessageAt?: number;
  stockStatus?: 'normal' | 'low' | 'out_of_stock' | 'ordered';
  createdAt?: number;
}

// --- STOCK REPORTS (employee reports for low/out of stock items) ---

export interface StockReport {
  reportId: string;
  tenantId: string;
  itemId: string;
  itemType: 'ingredient' | 'supply';
  itemName: string;           // Denormalized for display
  itemImageUrl?: string;      // Item image for display
  userId: string;
  userName: string;
  userEmail?: string;
  message?: string;           // Optional message from employee
  timestamp: number;          // Unix timestamp (sort key)
  status: 'pending' | 'approved' | 'rejected';
  // Quantity reporting
  reportType: 'out_of_stock' | 'low_stock' | 'quantity_correction' | 'expired';
  reportedQuantity: number;   // What employee says they counted
  systemQuantity: number;     // What system showed at time of report
  unitAbbreviation?: string;  // Unit abbreviation for display
  isUrgent?: boolean;         // True for expired items - requires immediate attention
  // Variation-specific fields (for supplies with sizes)
  sizeId?: string;            // Supply size/variation ID (if reporting on specific variation)
  sizeName?: string;          // Supply size/variation name for display
  // Admin response
  adminResponse?: string;
  adminRespondedAt?: number;
  adminRespondedBy?: string;
  // For approved reports, the new quantity that was applied
  appliedQuantity?: number;
}

// --- ORDER LISTS (admin shopping/restock lists) ---

export type OrderListColor = 'red' | 'orange' | 'amber' | 'yellow' | 'lime' | 'green' | 'emerald' | 'teal' | 'cyan' | 'sky' | 'blue' | 'indigo' | 'violet' | 'purple' | 'fuchsia' | 'pink' | 'rose' | 'slate' | 'gray';

export interface OrderListItem {
  id: string;
  itemId: string;              // Reference to ingredient or supply
  itemType: 'ingredient' | 'supply';
  itemName: string;            // Denormalized for display
  itemImageUrl?: string;       // Item image
  quantity: number;            // Quantity to order
  unitAbbreviation?: string;   // Unit for display
  note?: string;               // Additional note
  addedAt: number;             // Timestamp when added
  addedBy: string;             // User who added it
  fromReportId?: string;       // If added from a stock report
  isChecked?: boolean;         // For checklist functionality
}

export interface OrderList {
  id: string;
  tenantId: string;
  name: string;                // e.g., "Walmart", "Online Order", "Tomorrow Milk"
  color: OrderListColor;       // List color for visual distinction
  items: OrderListItem[];      // Items in this list
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
  order?: number;              // Display order
  isArchived?: boolean;        // Soft delete / archive
}

// Supply selection per meal size
export interface SupplyPerMealSize {
  supplyId: string;       // Which supply (e.g., Paper Cup)
  supplySizeId: string;   // Which supply variation (e.g., 12oz, 16oz)
  quantity: number;       // How many (default: 1)
}

export interface RecipeSupply {
  id: string;
  // Per meal size: which supply and variation to use
  // Key is meal size ID (e.g., "reg", "large"), value is supply selection
  perMealSize: Record<string, SupplyPerMealSize>;
  // Legacy fields for backward compatibility
  supplyId?: string;
  sizeId?: string;
  quantity?: number;
  quantityPerSize?: Record<string, number>;
}

export interface RecipeSize {
  id: string;
  name: string;
  retailPrice?: number; // Retail/selling price for this size
}

export interface RecipeIngredient {
  id: string;
  inventoryItemId: string;
  quantityPerSize: Record<string, number>; 
}

export interface RecipeStep {
  id: string;
  order: number;
  description: string;
  imageUrl?: string;
  ingredients: RecipeIngredient[];
  supplies?: RecipeSupply[];  // Supplies used in this step (cups, lids, etc.)
}

export interface Recipe {
  id: string;
  tenantId: string;
  title: string;
  description: string;
  categoryId: string;
  folderId?: string;  // Optional folder for organization
  imageUrl?: string;
  videoUrl?: string;
  price?: number;
  sizes: RecipeSize[];
  steps: RecipeStep[];
  createdBy: string;
  createdAt: number;
  updatedAt?: number;
  publishedAt?: number;
  status?: 'draft' | 'published';
  /** Internal memo/note visible only in Menu Hub (not visible to employees) */
  memo?: string;
  /** Timestamp of last auto-save */
  lastAutoSaved?: number;
}

/** MenuItem is an alias for Recipe - used in composition wizard */
export type MenuItem = Recipe;

/** Folder for organizing menu items */
export interface MenuFolder {
  id: string;
  tenantId: string;
  name: string;
  color?: string;  // Optional color for visual distinction
  icon?: string;   // Optional emoji or icon name
  order?: number;  // Display order
  createdAt: number;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
}

// --- DAILY ROUTINES ---

export type RoutineTaskType = 'general' | 'recipe' | 'cleaning' | 'special' | 'prep' | 'inventory' | 'safety';

export interface RoutineTask {
  id: string;
  time: string;
  type: RoutineTaskType;
  description: string;
  recipeId?: string;
  quantity?: number;
}

export interface RoutineDefinition {
  id: string;
  tenantId: string;
  name: string;
  tasks: RoutineTask[];
}

export interface RoutineAssignment {
  id: string;
  tenantId: string;
  employeeId: string;
  routineDefinitionId: string;
  date: string;
  completedTaskIds: string[];
}

// --- SCHEDULING ---

export interface Shift {
  id: string;
  tenantId: string;
  employeeId: string;
  start: string;
  end: string;
}

export interface TimeOffRequest {
  id: string;
  tenantId: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
}

export interface EmployeeAvailability {
  employeeId: string;
  tenantId: string;
  unavailableDays: number[];
}

export interface ScheduleGroup {
  id: string;
  tenantId: string;
  name: string;
  startDate: string;
  endDate: string;
  status?: 'draft' | 'published';
  publishedAt?: number;
  publishedBy?: string;
  createdBy?: string;
}

export interface StaffNotification {
  id: string;
  tenantId: string;
  type: 'timeoff_request' | 'availability_change';
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  timeOffId?: string;
  message: string;
  details: {
    startDate?: string;
    endDate?: string;
    duration?: string;
    reason?: string;
    requestStatus?: string;
    oldUnavailableDays?: string;
    newUnavailableDays?: string;
    changeType?: string;
  };
  status: 'unread' | 'actioned' | 'dismissed';
  actionType?: 'approved' | 'denied';
  createdAt: number;
  expiresAt?: number;
}

// --- SETTINGS ---

export interface BusinessProfile {
  name: string;
  address: string;
  phone: string;
  email: string;
  taxId: string;
  logoUrl?: string;
  themeColor: 'latte' | 'caramel' | 'tawny' | 'chestnut' | 'cocoa' | 'mocha' | 'espresso' | 'umber' | 'obsidian' | 'midnight';
  notificationEmails?: string[];
  // Owner fields
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  // Store hours for scheduling
  storeHours?: {
    openTime: string;  // HH:MM format e.g. "09:00"
    closeTime: string; // HH:MM format e.g. "21:00"
    weekStartDay?: number; // 0 = Sunday, 1 = Monday
  };
}

export interface SubscriptionSummary {
  id: string;
  status: string;
  customer?: string;
  current_period_end?: number;
  current_period_start?: number;
  cancel_at_period_end?: boolean;
  created?: number;
  /**
   * `GET /admin/subscriptions/{id}` returns Stripe's subscription VERBATIM, so
   * everything below is already on the wire — this type simply did not name it.
   * Widened rather than replaced: every field that was here still is.
   *
   * `metadata.skuId` is what lets a package item be resolved to its name; under
   * per-item billing a subscription carries one item per package, and without
   * the sku they are thirteen indistinguishable amounts.
   */
  items?: {
    data: Array<{
      id?: string;
      quantity?: number;
      metadata?: Record<string, string>;
      price?: { id?: string; nickname?: string; unit_amount?: number; currency?: string; metadata?: Record<string, string> };
      discounts?: Array<string | { id?: string; coupon?: { id?: string; amount_off?: number; duration_in_months?: number } }>;
    }>;
  };
  /** Present when a subscription schedule drives this subscription's phases. */
  schedule?: string | { id?: string } | null;
  discount?: { coupon?: { id?: string; amount_off?: number; duration_in_months?: number } } | null;
}

export interface ChargeSummary {
  id: string;
  charge_id?: string; // Alias for id (backward compatibility)
  amount: number;
  currency: string;
  status: string;
  paid: boolean;
  created: number;
  description?: string;
  receipt_url?: string;
  customer?: string;
  invoice?: string;
  refunded?: boolean;
  amount_refunded?: number;
}

export interface UpcomingInvoice {
  amount_due: number;
  currency: string;
  next_payment_attempt?: number;
  lines?: { data: any[] };
}

export interface PaymentEvent {
  tenantId: string;
  eventTimestamp: number;
  type: string;
  invoiceId?: string;
  subscriptionId?: string;
  amountPaid?: number;
  amountDue?: number;
  amount?: number;
  chargeId?: string;
  refundId?: string;
  currency?: string;
  status?: string;
  graceUntil?: number | null;
  reason?: string;
}

export interface TenantStatusInfo {
  status: string;
  subscriptionStatus?: string;
  graceUntil?: number | null;
  currentPeriodEnd?: number | null;
  cancelAtPeriodEnd?: boolean | null;
}

export interface SupportTicket {
  ticketId: string;
  tenantId: string;
  email: string;
  reason: string;
  status: string;
  createdAt: number;
}

export interface TicketMessage {
  ticketId: string;
  createdAt: number;
  sender: 'admin' | 'tenant' | string;
  message: string;
}

// Billing types for AdminBilling and TenantBilling pages
export interface SubscriptionInfo {
  tenantId: string;
  subscriptionStatus: string;
  currentPlan: string;
  hasSubscription: boolean;
  subscriptionId?: string;
  status?: string;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  planName?: string;
  planPrice?: number;
  planCurrency?: string;
  planInterval?: string;
  cancellationRequestedAt?: number | null;
  cancellationRequestStatus?: string | null;
  cancellationDeniedReason?: string;
}

export interface Invoice {
  id: string;
  invoiceId: string;
  number?: string;
  status: string;
  amount: number;
  amountPaid: number;
  amountDue: number;
  currency: string;
  periodStart: number;
  periodEnd: number;
  created: number;
  paidAt?: number;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

export interface PaymentHistory {
  id: string;
  date: string;
  description: string;
  amount?: number;
  status?: string;
  tenantId?: string;
  eventTimestamp?: number;
  type?: string;
}
